import { createRoot } from "react-dom/client";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ClerkProvider } from "@clerk/clerk-react";
import { ClerkAvailableProvider } from "./clerk-context.js";
import { WebsiteShell } from "./website-shell.js";
import { LandingPage } from "./pages/landing-page.js";
import { MarketplacePage } from "./pages/marketplace-page.js";
import { MarketplaceCategoryPage } from "./pages/marketplace-category-page.js";
import { GuideDetailPage } from "./pages/guide-detail-page.js";
import { CompanyPage } from "./pages/company-page.js";
import { DashboardPage } from "./pages/dashboard-page.js";
import { AuthPage } from "./pages/auth-page.js";
import { SkillsPage } from "./pages/skills-page.js";
import { DesignLayout } from "./design/DesignLayout.js";
import DesignApp from "./design/App.jsx";
import DesignLogoPage from "./design/LogoPage.jsx";
import DesignLandingPage from "./design/LandingPage.jsx";
import DesignMarketplacePage from "./design/MarketplacePage.jsx";
import DesignMarketplaceGuidePage from "./design/MarketplaceGuidePage.jsx";
import DesignWebappPage from "./design/WebappPage.jsx";
import DesignWebappSkillsPage from "./design/WebappSkillsPage.jsx";
import DesignMobilePage from "./design/MobilePage.jsx";
import DesignFlowsPage from "./design/FlowsPage.jsx";
import DesignScreenMapPage from "./design/ScreenMapPage.jsx";
import DesignDemosPage from "./design/DemosPage.jsx";
import { marketplaceGuides } from "./design/data/marketplaceGuides.js";
import "./index.css";

const publishableKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as string | undefined;

function AppRoutes() {
  return (
    <BrowserRouter>
      <Routes>
        <Route element={<WebsiteShell />}>
          <Route path="/" element={<LandingPage />} />
          <Route path="/marketplace" element={<MarketplacePage />} />
          <Route path="/marketplace/category/:slug" element={<MarketplaceCategoryPage />} />
          <Route path="/marketplace/:slug" element={<GuideDetailPage />} />
          <Route path="/skills" element={<SkillsPage />} />
          <Route path="/company" element={<CompanyPage />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/sign-in/*" element={<AuthPage mode="sign-in" />} />
          <Route path="/sign-up/*" element={<AuthPage mode="sign-up" />} />
        </Route>
        <Route path="/private/design" element={<DesignLayout />}>
          <Route index element={<DesignApp />} />
          <Route path="logo" element={<DesignLogoPage />} />
          <Route path="landing" element={<DesignLandingPage />} />
          <Route path="marketplace" element={<DesignMarketplacePage />} />
          {marketplaceGuides.map((guide) => (
            <Route
              key={guide.slug}
              path={`marketplace/${guide.slug}`}
              element={<DesignMarketplaceGuidePage slug={guide.slug} />}
            />
          ))}
          <Route path="webapp" element={<DesignWebappPage />} />
          <Route path="webapp/skills" element={<DesignWebappSkillsPage />} />
          <Route path="mobile" element={<DesignMobilePage />} />
          <Route path="flows" element={<DesignFlowsPage />} />
          <Route path="screen-map" element={<DesignScreenMapPage />} />
          <Route path="demos" element={<DesignDemosPage />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}

const root = document.getElementById("root");
if (root) {
  createRoot(root).render(
    publishableKey ? (
      <ClerkProvider
        publishableKey={publishableKey}
        signInUrl="/sign-in"
        signUpUrl="/sign-up"
        signInFallbackRedirectUrl="/dashboard"
        signUpFallbackRedirectUrl="/dashboard"
      >
        <ClerkAvailableProvider value={true}>
          <AppRoutes />
        </ClerkAvailableProvider>
      </ClerkProvider>
    ) : (
      <ClerkAvailableProvider value={false}>
        <AppRoutes />
      </ClerkAvailableProvider>
    ),
  );
}
